'use client';
/** @jsxImportSource @emotion/react */

import React, {
  type ElementType,
  forwardRef,
  type Ref,
  useContext,
} from 'react';

import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import { alpha, useTheme } from '@mui/material';
import MuiButton, {
  buttonClasses,
  type ButtonTypeMap as MuiButtonTypeMap,
} from '@mui/material/Button';
import { ButtonGroupContext } from '@mui/material/ButtonGroup';

import { CircularProgress } from '../progress/CircularProgress';
import { isEmptyObject } from '../utils/isEmptyObject';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    forceWhite: true;
  }
}

type ExcludedMuiProps =
  | 'color'
  | 'disableElevation'
  | 'disableFocusRipple'
  | 'disableRipple';

type CustomProps = {
  color?: Exclude<MuiButtonProps['color'], 'inherit'>;
  component?: ElementType;
  /**
   * If `true`, the button will be disabled and display a loading indicator
   */
  loading?: boolean;
};

type ButtonTypeMap<
  P = CustomProps,
  D extends ElementType = MuiButtonTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiButtonTypeMap['props'], ExcludedMuiProps> & P;
};

/**
 * @deprecated Please use `Button` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-button-migration--button-migration-guide Migration guide}
 */
export type ButtonProps<
  D extends ElementType = MuiButtonTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiButtonTypeMap<P, D>, D>;

const iconSizes: Record<NonNullable<ButtonProps['size']>, number> = {
  large: 24,
  medium: 20,
  small: 18,
};

// The offset values are based on MUI icon positions to ensure consistency.
// The `text` variant has different widths (padding), so it requires separate adjustments.
const loadingIndicatorOffsets: Record<
  NonNullable<ButtonProps['size']>,
  { default: string; text: string }
> = {
  large: { default: '18px', text: '8px' },
  medium: { default: '14px', text: '6px' },
  small: { default: '10px', text: '6px' },
};

const TRANSPARENT = 'transparent';

const ButtonComponent = (
  {
    children,
    disabled,
    loading,
    size,
    color,
    variant,
    sx,
    startIcon,
    endIcon,
    ...props
  }: ButtonProps,
  ref: Ref<HTMLButtonElement>
) => {
  const buttonGroupContext = useContext(ButtonGroupContext);
  // The default value of ButtonGroupContext is {}, so we need to check if it has any keys to determine if the button is actually inside a ButtonGroup.
  const isInButtonGroup =
    buttonGroupContext && !isEmptyObject(buttonGroupContext);

  // default loading option - no icons or both icons
  const centeredLoading = (startIcon && endIcon) || (!startIcon && !endIcon);
  // start icon only
  const leftLoading = startIcon && !endIcon;
  // end icon only
  const rightLoading = endIcon && !startIcon;

  // We need concrete values to calculate the loading styles (spinner color, background).
  // Since we intentionally don't set default values in the component props (to allow MuiButton to inherit from ButtonGroupContext),
  // we must provide the standard MUI defaults ('primary', 'medium', and 'text' or 'outlined' for ButtonGroup) here for our calculations.
  const effectiveColor = color || 'primary';
  const effectiveVariant =
    variant ||
    buttonGroupContext?.variant ||
    (isInButtonGroup ? 'outlined' : 'text');
  const effectiveSize = size || buttonGroupContext?.size || 'medium';
  const loadingOffset =
    loadingIndicatorOffsets[effectiveSize][
      effectiveVariant === 'text' ? 'text' : 'default'
    ];

  // In ButtonGroup, loading buttons should keep disabled styles.
  // ButtonGroup rarely uses loading state, and implementing consistent loading styles
  // across the group is complex. We'll address this uniformly after migrating to Next.
  const shouldUseDisabledStyle = disabled || isInButtonGroup;

  const theme = useTheme();
  const getSpinnerColor = () => {
    if (shouldUseDisabledStyle) {
      return theme.palette.action.disabled;
    }

    return effectiveVariant === 'contained'
      ? theme.palette[effectiveColor].contrastText
      : theme.palette[effectiveColor].main;
  };

  return (
    <MuiButton
      {...props}
      color={color}
      variant={variant}
      startIcon={startIcon}
      endIcon={endIcon}
      ref={ref}
      children={[
        loading ? (
          <CircularProgress
            key="progress"
            size={iconSizes[effectiveSize]}
            sx={{
              color: getSpinnerColor(),
              position: 'absolute',
              ...(leftLoading && {
                left: loadingOffset,
              }),
              ...(rightLoading && {
                right: loadingOffset,
              }),
            }}
          />
        ) : null,
        <span key="value">{children}</span>,
      ]}
      disabled={disabled || loading}
      size={size}
      sx={[
        ...(Array.isArray(sx) ? sx : [sx]),
        () => {
          if (!loading) {
            return {};
          }

          const getLoadingStyles = () => {
            if (shouldUseDisabledStyle) {
              return {};
            }

            const mainColor = theme.palette[effectiveColor].main;
            const contrastColor = theme.palette[effectiveColor].contrastText;
            const borderColor = alpha(mainColor, 0.5);
            const textColor = centeredLoading
              ? TRANSPARENT
              : effectiveVariant === 'contained'
                ? contrastColor
                : mainColor;

            const styleMap = {
              contained: {
                color: textColor,
                backgroundColor: mainColor,
              },
              outlined: {
                color: textColor,
                borderColor,
              },
              text: {
                color: textColor,
              },
            };

            return styleMap[effectiveVariant];
          };

          return {
            ...(startIcon && {
              [`.${buttonClasses.startIcon}`]: {
                visibility: 'hidden',
              },
            }),
            ...(endIcon && {
              [` .${buttonClasses.endIcon}`]: {
                visibility: 'hidden',
              },
            }),
            ...(centeredLoading && {
              [`&.${buttonClasses.root}`]: {
                color: TRANSPARENT,
              },
            }),
            ...(loading &&
              !shouldUseDisabledStyle && {
                '&.Mui-disabled': {
                  ...getLoadingStyles(),
                },
              }),
          };
        },
      ]}
    />
  );
};

ButtonComponent.displayName = 'Button';

/**
 * @deprecated Please use `Button` from `@zeus/core/next` instead.
 * @see {@link https://zeus.serko.travel/?path=/docs/zeus-core-next-components-button-migration--button-migration-guide Migration guide}
 */
export const Button = forwardRef(
  ButtonComponent
) as OverridableComponent<ButtonTypeMap>;
